import { useMemo, useState } from "react";
import {
  IconCalendarEvent,
  IconChevronDown,
  IconPencil,
  IconPlus,
} from "@tabler/icons-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PageHeader } from "@/components/ui/page-header";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import { useEvents } from "@/api/hooks/use-events";
import { useBoats } from "@/api/hooks/use-boats";
import { useAdminEventsActions } from "@/hooks/use-admin-events-actions";
import { EventFormDialog } from "./admin-events/event-form-dialog";
import { EventBuildsPanel } from "./admin-events/event-builds-panel";
import type { components } from "@/api/generated/schema";
import { currentSeason, groupBySeason } from "@/lib/season";

type EventResponse = components["schemas"]["EventResponse"];

function formatRange(start: string, end?: string | null): string {
  const fmt = (d: string) =>
    new Date(d + "T00:00:00").toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  if (!end || end === start) return fmt(start);
  const s = new Date(start + "T00:00:00").toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
  return `${s} – ${fmt(end)}`;
}

export function AdminEventsPage() {
  const { data: events, isLoading } = useEvents();
  const { data: boats } = useBoats();
  const actions = useAdminEventsActions();

  const [createOpen, setCreateOpen] = useState(false);
  const [editEvent, setEditEvent] = useState<EventResponse | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Pre-fill the create form with the CURRENT season — date-based, shared with
  // the sidebar switcher (S6 launch fallback). Not Math.max: with S7 already
  // imported, the max is a season that hasn't started yet.
  const defaultSeason = useMemo(() => currentSeason(events ?? []) ?? 6, [events]);

  // Grouped by season (newest first) so the list stays legible once it spans
  // many seasons; groupBySeason also sorts events within a season newest-first.
  const seasonGroups = useMemo(() => groupBySeason(events ?? []), [events]);
  const hasEvents = (events?.length ?? 0) > 0;

  // Season filter: null until the user picks, so the default tracks the
  // CURRENT season as events load (same date-based rule as the sidebar
  // switcher); "all" shows every season.
  const [seasonPick, setSeasonPick] = useState<string | null>(null);
  const seasonFilter = seasonPick ?? String(defaultSeason);
  const visibleGroups =
    seasonFilter === "all"
      ? seasonGroups
      : seasonGroups.filter((g) => String(g.season) === seasonFilter);

  function toggleExpanded(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  if (isLoading) {
    return (
      <div>
        <Skeleton className="h-8 w-48 rounded-lg" />
        <div className="mt-6 space-y-2">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-14 w-full rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title="Manage Events"
        action={
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <IconPlus className="mr-1.5 h-4 w-4" />
            New Event
          </Button>
        }
      />

      {hasEvents && (
        <div className="mt-4 flex items-center gap-2">
          <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-[0.08em]">
            Season
          </span>
          <Select value={seasonFilter} onValueChange={setSeasonPick}>
            <SelectTrigger size="sm" className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {seasonGroups.map((g) => (
                <SelectItem key={g.season} value={String(g.season)}>
                  Season {g.season}
                  {g.season === defaultSeason ? " (current)" : ""}
                </SelectItem>
              ))}
              <SelectItem value="all">All seasons</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="mt-6">
        {!hasEvents ? (
          <EmptyState
            icon={IconCalendarEvent}
            message="No events yet"
            className="py-16"
          />
        ) : (
          <div className="flex flex-col gap-6">
            {visibleGroups.map((group) => (
              <div key={group.season}>
                <div className="px-1 pb-2 text-[11px] font-semibold text-muted-foreground uppercase tracking-[0.08em]">
                  Season {group.season}
                </div>
                <div className="flex flex-col gap-2">
                  {group.events.map((event) => {
                    const isOpen = expanded.has(event.id);
                    return (
                      <div
                        key={event.id}
                        className="bg-card border-l-3 border-l-border rounded-r-lg transition-shadow hover:shadow-[0_1px_4px_rgba(0,0,0,0.04)]"
                      >
                        <div className="flex items-center pl-4 pr-3 py-3">
                          <button
                            onClick={() => toggleExpanded(event.id)}
                            className="flex flex-1 items-center gap-3 min-w-0 text-left"
                          >
                            <IconChevronDown
                              className={`h-4 w-4 text-muted-foreground shrink-0 transition-transform ${
                                isOpen ? "" : "-rotate-90"
                              }`}
                            />
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium">
                                  {event.name}
                                </span>
                                <span className="font-mono text-xs bg-muted px-2 py-0.5 rounded">
                                  S{event.season}
                                </span>
                              </div>
                              <div className="mt-1 text-[11px] text-muted-foreground">
                                {formatRange(event.start_date, event.end_date)}
                                <span className="text-muted-foreground/40 mx-2">
                                  |
                                </span>
                                {event.location}
                                {event.timezone && (
                                  <>
                                    <span className="text-muted-foreground/40 mx-2">
                                      |
                                    </span>
                                    {event.timezone}
                                  </>
                                )}
                              </div>
                            </div>
                          </button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditEvent(event)}
                            className="shrink-0 text-muted-foreground hover:text-foreground"
                          >
                            <IconPencil className="h-4 w-4" />
                          </Button>
                        </div>

                        {isOpen && (
                          <div className="border-t">
                            <EventBuildsPanel
                              eventId={event.id}
                              boats={boats ?? []}
                              actions={actions}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <EventFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        event={null}
        boats={boats ?? []}
        defaultSeason={defaultSeason}
        existingEvents={events ?? []}
        actions={actions}
        onCreated={(id) => setExpanded((prev) => new Set(prev).add(id))}
      />

      <EventFormDialog
        open={editEvent !== null}
        onOpenChange={(o) => !o && setEditEvent(null)}
        event={editEvent}
        boats={boats ?? []}
        defaultSeason={defaultSeason}
        existingEvents={events ?? []}
        actions={actions}
        onCreated={() => {}}
      />
    </>
  );
}
